import { Alert, Form, Input, Message, Modal, Radio, Typography } from '@arco-design/web-react';
import { useEffect, useState } from 'react';
import { customersApi } from '@/api';
import type { CustomerSummary } from '@/types/customer';

interface Props {
  customer: CustomerSummary | null;
  visible: boolean;
  onClose: () => void;
}

interface FormState {
  mode: 'auto' | 'manual';
  newPassword?: string;
  reason: string;
}

export default function ResetPasswordModal({ customer, visible, onClose }: Props) {
  const [form] = Form.useForm<FormState>();
  const [generated, setGenerated] = useState<string | null>(null);
  const mode = Form.useWatch('mode', form);

  useEffect(() => {
    if (visible) {
      form.resetFields();
      form.setFieldsValue({ mode: 'auto' });
      setGenerated(null);
    }
  }, [visible, form]);

  const handleOk = async () => {
    if (!customer) return;
    if (generated) {
      onClose();
      return;
    }
    const values = await form.validate();
    const resp = await customersApi.resetPassword({
      customerId: customer.id,
      newPassword: values.mode === 'manual' ? values.newPassword : undefined,
      reason: values.reason.trim(),
    });
    if (resp.newPassword) {
      setGenerated(resp.newPassword);
      Message.success('已生成新密码，请妥善转交客户');
    } else {
      Message.success('登录密码已重置');
      onClose();
    }
  };

  return (
    <Modal
      title={`重置「${customer?.username ?? ''}」的登录密码`}
      visible={visible}
      onOk={handleOk}
      onCancel={onClose}
      okText={generated ? '我已记录，关闭' : '确认重置'}
      cancelText="取消"
      maskClosable={false}
    >
      {generated ? (
        <Alert
          type="warning"
          content={
            <div>
              <div style={{ marginBottom: 8 }}>新密码（仅本次展示）：</div>
              <Typography.Text copyable className="admin-mono" style={{ fontSize: 16 }}>
                {generated}
              </Typography.Text>
            </div>
          }
        />
      ) : (
        <Form form={form} layout="vertical">
          <Form.Item label="设置方式" field="mode" rules={[{ required: true }]}>
            <Radio.Group type="button">
              <Radio value="auto">系统生成随机密码</Radio>
              <Radio value="manual">管理员指定新密码</Radio>
            </Radio.Group>
          </Form.Item>
          {mode === 'manual' && (
            <Form.Item
              label="新密码"
              field="newPassword"
              rules={[
                { required: true, message: '请输入新密码' },
                { minLength: 8, message: '密码至少 8 位' },
              ]}
            >
              <Input.Password placeholder="至少 8 位，建议含字母+数字" />
            </Form.Item>
          )}
          <Form.Item
            label="操作原因（将写入审计日志）"
            field="reason"
            rules={[
              { required: true, message: '请填写原因' },
              { minLength: 4, message: '原因至少 4 个字符' },
            ]}
          >
            <Input.TextArea autoSize={{ minRows: 2, maxRows: 4 }} maxLength={200} showWordLimit />
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
}
